import React, { useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useQuery } from "@tanstack/react-query"

/**
 * Kategorie-Chip mit Tooltip „Üblich bei solchen Mails“ (#83).
 *
 * Robin/Paul, 08.09.2026: alles, was wir zu einem Mandanten wissen, so
 * aufbereiten, dass jeder Mitarbeiter an der Mail sieht, was in der Regel zu
 * tun ist. Die Daten kommen EINMAL je Mandant (`/api/v1/context/vorgehen`),
 * nicht je Mail — sonst löste die Liste hundert Anfragen aus.
 */
export type VorgehenFakt = { label: string; n: number; prozent: number }
export type VorgehenEintrag = {
  kategorie: string; gewerk: string; n: number
  fakten: VorgehenFakt[]; ueblich: string; regel?: string | null
  /** Vorschlag „So machen wir das“ aus der Historie — grau, bis jemand ihn übernimmt. */
  vorschlag?: string | null
}
export type VorgehenDossier = {
  tenant_slug: string; tenant_name: string; tage: number; mails: number
  eintraege: VorgehenEintrag[]
  regeln: { kategorie: string; gewerk: string; text: string }[]
  /** Wann die Vorschläge zuletzt erzeugt wurden; null = noch nie. */
  vorschlaege_stand?: string | null
}

export function useVorgehen(tenantSlug?: string | null) {
  return useQuery<VorgehenDossier>({
    queryKey: ["vorgehen", tenantSlug || ""],
    queryFn: async () => {
      const r = await fetch(`/api/v1/context/vorgehen?tenant=${encodeURIComponent(tenantSlug || "")}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
    enabled: !!tenantSlug,
    staleTime: 1000 * 60 * 5,
  })
}

/** Erst Kategorie × Gewerk, dann Kategorie — dieselbe Regel wie im Backend. */
export function findeVorgehen(d: VorgehenDossier | undefined, kategorie?: string | null, gewerk?: string | null): VorgehenEintrag | null {
  if (!d || !kategorie) return null
  const kl = kategorie.trim().toLowerCase()
  const gl = (gewerk || "").trim().toLowerCase()
  if (gl) {
    const fein = d.eintraege.find((e) => e.kategorie.toLowerCase() === kl && e.gewerk.toLowerCase() === gl)
    if (fein) return fein
  }
  return d.eintraege.find((e) => e.kategorie.toLowerCase() === kl && !e.gewerk) || null
}

export function VorgehenChip({
  tenantSlug, kategorie, gewerk, className, style, mitIcon = false,
}: {
  tenantSlug?: string | null
  kategorie: string
  gewerk?: string | null
  className?: string
  style?: React.CSSProperties
  /** Listenzeile: ein kleines ⓘ als Anker, falls der Chip selbst ausgeblendet
   *  ist (enge Liste blendet `.ev2-row-cat` unter 420 px aus, seit 23.08.). */
  mitIcon?: boolean
}) {
  const [offen, setOffen] = useState(false)
  const anker = useRef<HTMLSpanElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const q = useVorgehen(offen ? tenantSlug : null)
  // Fest positioniert ueber ein Portal: die Liste ist ein Scroll-Container
  // und wuerde einen absolut positionierten Tooltip abschneiden.
  useLayoutEffect(() => {
    if (!offen || !anker.current) { setPos(null); return }
    const r = anker.current.getBoundingClientRect()
    const breite = 340
    const left = Math.max(8, Math.min(r.right - breite, (typeof window !== "undefined" ? window.innerWidth : 1200) - breite - 8))
    setPos({ top: r.bottom + 6, left })
  }, [offen])
  const eintrag = findeVorgehen(q.data, kategorie, gewerk)
  const pflegeHref = `/context${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`
  const titel = eintrag
    ? `Üblich bei ${eintrag.kategorie}${eintrag.gewerk ? ` · ${eintrag.gewerk}` : ""}`
    : `Üblich bei ${kategorie}`

  return (
    <span
      ref={anker}
      style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4, maxWidth: "100%" }}
      onMouseEnter={() => setOffen(true)}
      onMouseLeave={() => setOffen(false)}
      onFocus={() => setOffen(true)}
      onBlur={() => setOffen(false)}
      tabIndex={0}
      aria-describedby={offen ? `vorgehen-${kategorie}` : undefined}
    >
      <span className={className} style={{ ...(style || {}), cursor: "help" }}>{kategorie}</span>
      {mitIcon && (
        <span className="ev2-row-vorgehen-icon" aria-label={`Üblich bei ${kategorie}`} title="">ⓘ</span>
      )}
      {offen && typeof document !== "undefined" && createPortal(
        <div
          role="tooltip"
          id={`vorgehen-${kategorie}`}
          data-testid="vorgehen-tooltip"
          onClick={(ev) => ev.stopPropagation()}
          style={{
            position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, zIndex: 9999,
            width: 340, padding: "10px 12px", textAlign: "left",
            background: "var(--bg-card)", border: "1px solid var(--border-color)",
            borderRadius: 10, boxShadow: "0 10px 32px rgba(0,0,0,0.12)",
            fontSize: 12.5, lineHeight: 1.5, color: "var(--text)", fontWeight: 400, whiteSpace: "normal",
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{titel}</div>
          {q.isLoading && <div style={{ color: "var(--text-muted)" }}>Lädt …</div>}
          {q.isError && <div style={{ color: "var(--text-muted)" }}>Konnte nicht geladen werden.</div>}
          {q.data && !eintrag && (
            <div style={{ color: "var(--text-muted)" }}>
              Noch keine Erfahrung mit dieser Anfrageart in den letzten {q.data.tage} Tagen.
            </div>
          )}
          {eintrag && (
            <>
              {eintrag.regel && (
                <div style={{ marginBottom: 6, padding: "6px 8px", background: "var(--secondary-bg)", borderRadius: 6 }}>
                  <span style={{ fontWeight: 500 }}>So machen wir das: </span>{eintrag.regel}
                </div>
              )}
              {eintrag.n > 0 ? (
                <>
                  <div style={{ color: "var(--text-muted)", marginBottom: 2 }}>
                    In {eintrag.n} {eintrag.n === 1 ? "Fall" : "Fällen"} der letzten {q.data?.tage ?? 90} Tage:
                  </div>
                  {eintrag.fakten.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {eintrag.fakten.map((f) => (
                        <li key={f.label}>{f.label} <span style={{ color: "var(--text-muted)" }}>· {f.prozent} %</span></li>
                      ))}
                    </ul>
                  ) : (
                    <div>meist keine Aktion nötig</div>
                  )}
                </>
              ) : (
                <div style={{ color: "var(--text-muted)" }}>Noch keine Fälle in der Historie.</div>
              )}
            </>
          )}
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <a href={pflegeHref} style={{ color: "var(--accent-color)" }}>Spielregeln im Kontext-Center</a>
          </div>
        </div>,
        document.body,
      )}
    </span>
  )
}
